<!--
  Provides the template for the application title and summary details at the top of
  the application.
-->
<template>
  <div
    class="ApplicationSummary"
    v-if="
      model.length &&
      !state.isEditing &&
      !isSentToErn &&
      !isWithdrawn &&
      !isInvalid &&
      !isInternalTransfer
    "
  >
    <AdsButton
      v-if="showSendEmailToParent"
      class="secondaryBtn"
      :class="{ 'mr-2': showSendEmailToParent }"
      x-large
      secondary
      @click="openEmailParentCarerDialog"
      button-text="Email Parent/Carer"
      aria-label="sendSendEmailToParent"
      data-testid="sendEmailToParent-btn"
    />
    <AdsButton
      v-if="showSetToInProgress"
      class="secondaryBtn"
      :class="{ 'mr-2': showSetToInProgress }"
      x-large
      secondary
      @click="openSetToInProgressDialog"
      button-text="Set to In Progress"
      aria-label="showSetToInProgress"
      data-testid="showSetToInProgress-btn"
    />
    <AdsButton
      v-if="!hideWithdraw"
      class="secondaryBtn"
      :class="{ 'mr-2': !hideSendToErn }"
      x-large
      secondary
      @click="openWithdrawDialog"
      button-text="Withdraw"
      icon="remove_circle_outline"
      aria-label="Withdraw"
      data-testid="withdraw-btn"
    />
    <AdsButton
      v-if="showInternalTransferBtn && !isSRNLinked"
      class="secondaryBtn"
      :class="{ 'mr-2': !hideSendToErn }"
      x-large
      secondary
      @click="openInternalTransferModal"
      button-text="Mark as internal transfer"
      icon="mdi-cached"
      aria-label="Mark as internal transfer"
      data-testid="mark-as-internal-transfer-btn"
    />
    <AdsButton
      v-if="!hideSendToErn"
      x-large
      aria-label="Send to ERN"
      :disabled="isSendToErnDisabled"
      @click="sendToERN"
      button-text="Send to ERN"
      icon="mdi-send-outline"
      class="sendToErnButton"
      :loading="state.isSendingToErn && !isSentToErn"
    />
  </div>
</template>

<script>
import { AdsButton } from '@nswdoe/doe-ui-core'
import fieldHelperMixin from '@/helpers/fieldHelperMixin'
import { STREAM } from '@/constants'
export default {
  name: 'ApplicationSummary',
  mixins: [fieldHelperMixin],
  components: { AdsButton },
  props: {
    hideSendToErn: {
      type: Boolean,
      default: false
    },
    hideWithdraw: {
      type: Boolean,
      default: false
    },
    showSendEmailToParent: {
      type: Boolean,
      default: false
    },
    showSetToInProgress: {
      type: Boolean,
      default: false
    },
    showInternalTransferBtn: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    state() {
      return this.$store.state
    },
    contact() {
      return this.getValue('parentCarers').find(
        (parent) => parent.isEnrolmentOwner === true
      )
    },
    applicationId() {
      return this.getValue('applicationID')
    },
    isSendToErnDisabled() {
      // Prevent Send to ERN if any outstanding actions, or if no linked SRN.
      // Note: When sending to ERN there will always be a linked SRN, as this
      // will have been created by the SRN Search dialog if there was no existing
      // SRN to link to.
      return (
        this.totalAlerts > 0 ||
        (this.state.application && !this.state.application.student.srn)
      )
    },
    isSRNLinked() {
      return !!this.state.application?.student?.srn
    },
    showFamilyTreeLinkMessage() {
      const unmatchedErnRecord = this.model.filter(
        (modelRow) =>
          modelRow.section.id === 'parents' &&
          modelRow.isUnmatchedErnRecord &&
          !this.isWithdrawn
      )
      return unmatchedErnRecord.length > 0
    }
  },
  methods: {
    contactDetails(type) {
      var contactDetails = this.contact.contactDetails || []
      var phone = contactDetails.find((item) => item.contactType === type) || {}
      return phone.contactValue
    },
    sendToERN() {
      this.$store.dispatch(
        'sendApplicationToERN',
        this.showFamilyTreeLinkMessage
      )
    },
    openWithdrawDialog() {
      this.$store.dispatch('set', ['openWithdrawModal', true])
      this.$store.dispatch('set', ['applicationWithdrawn', 'pending'])
    },
    openEmailParentCarerDialog() {
      this.$store.dispatch('set', ['openEmailParentCarerModal', true])
      this.$store.dispatch('set', ['emailParentCarerStatus', 'pending'])
    },
    openSetToInProgressDialog() {
      this.$store.dispatch('set', ['openSetToInProgressModal', true])
      this.$store.dispatch('set', ['setToInProgressStatus', 'pending'])
    },
    async openInternalTransferModal() {
      await this.$store.dispatch('set', ['actionFromStream', STREAM.LOCAL_AREA])
      this.$store.dispatch('set', ['openInternalTransfer', true])
    }
  }
}
</script>

<style scoped lang="scss">
.ApplicationSummary {
  padding: 1.5rem 3rem 1.5rem 1rem;
  display: flex;
  justify-content: flex-end;
  flex-flow: row wrap;

  .summary {
    flex: 1;
    min-width: 350px;

    h1 {
      margin-top: 0;
      font-size: 1.5rem;
      font-weight: 400;

      span {
        margin-left: 20px;
      }
    }

    .fields {
      td {
        padding-bottom: 1rem;
        vertical-align: top;
      }
      .label {
        color: $color-text-light;
        white-space: nowrap;
        &:after {
          content: ':';
        }
        padding-right: 0.5rem;
      }
      .value {
        color: black;
        padding-right: 3rem;
      }
    }
  }
}
.secondaryBtn,
.sendToErnButton {
  margin: 0;
}

::v-deep .v-btn:not(.v-btn--round).v-size--x-large {
  height: 44px;
}

::v-deep
  .theme--light.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(
    .v-btn--outlined
  ) {
  background-color: $grey-darken1 !important;
  color: $ads-selection-control-gray !important;
  .v-icon {
    color: $ads-selection-control-gray !important;
  }
}

#app.v-application button.v-btn:not(.v-btn--round).review-btn {
  color: black;
}

#app.v-application button.v-btn.sendToErnButton:focus:before {
  opacity: 0; /* Overrides the opacity of other :focus styles on this specific button */
}
</style>
